// The rpg layer, drawn (phase D1), in the low-poly look on its one surface material: the forge (an anvil and a hearth
// of glowing coals) and the cooking pot (a cauldron over a fire); the merchant and the smith as townsfolk figures (the
// villager's and the elder's cards: stand-ins until the town stream's NPCs land); the Wanderer's scarf at the length
// its glyphs give (`Chain.setLinks`); and the level-up moment: a gold ring rising from the hero's feet and a notice
// line in the market lab (`data-rpg-notice`, through the strings table; in the valley the HUD's notice area says it).
// A view: it reads the sim and never writes it.
import * as THREE from "three";
import { facet, merge, seeded } from "@rpg/meshkit";
import type { LowPolyLook } from "@rpg/look";
import type { CollisionWorld } from "@rpg/collision";
import type { Figure } from "@rpg/figure";
import type { EventBus } from "@rpg/kernel";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { GameEvents } from "../core";
import type { Fight } from "../enemies/fight";
import { mountFigureSet, type FigureSet } from "../bodies/figures";
import type { Rpg } from "./module";

const C = (hex: string): THREE.Color => new THREE.Color(hex);
const part = (g: THREE.BufferGeometry, colour: string, seed: number, glow = 0): THREE.BufferGeometry => facet(g, { colour: C(colour), jitter: 0, tint: 0.08, rng: seeded(seed), glow });

/** The forge: a hearth of stone with glowing coals, an anvil on a stump beside it. */
function forgeGeometry(): THREE.BufferGeometry {
  return merge([
    part(new THREE.BoxGeometry(1.3, 0.8, 1.0).translate(0, 0.4, -0.2), "#7d7468", 1),
    part(new THREE.BoxGeometry(1.0, 0.12, 0.7).translate(0, 0.86, -0.2), "#e8743b", 2, 1),
    part(new THREE.IcosahedronGeometry(0.16, 0).translate(-0.2, 0.95, -0.2), "#f2c14e", 3, 1),
    part(new THREE.IcosahedronGeometry(0.14, 0).translate(0.22, 0.93, -0.12), "#e05a3c", 4, 1),
    part(new THREE.CylinderGeometry(0.12, 0.16, 1.4, 5).translate(0.45, 1.5, -0.45), "#5d564c", 5),
    part(new THREE.CylinderGeometry(0.28, 0.32, 0.5, 6).translate(0, 0.25, 0.85), "#8a6a4a", 6),
    part(new THREE.BoxGeometry(0.62, 0.18, 0.26).translate(0, 0.6, 0.85), "#4a4f55", 7),
    part(new THREE.ConeGeometry(0.13, 0.32, 4).rotateZ(Math.PI / 2).translate(0.45, 0.62, 0.85), "#4a4f55", 8),
  ]);
}

/** The cooking pot: a round cauldron on three legs over a small fire. */
function potGeometry(): THREE.BufferGeometry {
  const legs = [0, 1, 2].map((i) => {
    const a = (i / 3) * Math.PI * 2;
    return part(new THREE.CylinderGeometry(0.04, 0.04, 0.4, 4).translate(Math.sin(a) * 0.36, 0.2, Math.cos(a) * 0.36), "#3e3a36", 10 + i);
  });
  const flames = [0, 1, 2, 3].map((i) => {
    const a = (i / 4) * Math.PI * 2 + 0.4;
    return part(new THREE.ConeGeometry(0.09, 0.3, 4).translate(Math.sin(a) * 0.12, 0.15, Math.cos(a) * 0.12), i % 2 ? "#f2c14e" : "#e8743b", 20 + i, 1);
  });
  return merge([
    ...legs,
    ...flames,
    part(new THREE.SphereGeometry(0.46, 8, 6, 0, Math.PI * 2, Math.PI * 0.35, Math.PI * 0.65).translate(0, 0.78, 0), "#3e3a36", 30),
    part(new THREE.TorusGeometry(0.34, 0.05, 4, 10).rotateX(Math.PI / 2).translate(0, 1.05, 0), "#5d564c", 31),
    part(new THREE.CircleGeometry(0.31, 9).rotateX(-Math.PI / 2).translate(0, 1.0, 0), "#9fbf5a", 32, 0.3),
  ]);
}

/** The view's report for the handle. */
export interface RpgView {
  report(): { stations: number; npcs: number; scarfLinks: number | null; notices: number; rings: number };
  /** Clear what the warm-up showed. */
  clearWarm(): void;
}

/** Mount the rpg layer's view; call it inside the valley look's warm-up (its `warm` hook), so its programs compile then. */
export function mountRpgView(o: {
  runtime: Runtime;
  scene: THREE.Scene;
  look: LowPolyLook;
  content: SpikeContent;
  rpg: () => Rpg;
  fight: () => Fight;
  collision: () => CollisionWorld;
  probe: (x: number, z: number, fromY: number) => number | null;
  hero: { figure: Figure };
  events: EventBus<GameEvents>;
  /** Draw its own level-up notice line (the market lab); false where the HUD's notice area says it (the valley). */
  notice?: boolean;
}): RpgView {
  const r = o.rpg();
  const root = new THREE.Group();
  root.name = "rpg";
  o.scene.add(root);
  const mat = o.look.surface;
  // the stations
  const geos = { forge: forgeGeometry(), pot: potGeometry() };
  let stations = 0;
  for (const m of r.places) {
    if (m.kind !== "station" || !m.station) continue;
    const mesh = new THREE.Mesh(geos[m.station], mat);
    mesh.position.set(m.pos[0]!, o.probe(m.pos[0]!, m.pos[1]!, 5) ?? 0, m.pos[1]!);
    mesh.rotation.y = m.yaw;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.name = `station:${m.id}`;
    root.add(mesh);
    stations++;
  }
  o.look.track(root);
  // the NPC stand-ins as townsfolk figures (ids out of the fight's range)
  const npcs = r.places.filter((m) => m.kind === "npc" && m.card);
  const npcSet: FigureSet = mountFigureSet({
    runtime: o.runtime,
    scene: o.scene,
    look: o.look,
    content: o.content,
    bodies: () => o.fight().bodies,
    collision: o.collision,
    entries: () => npcs.map((m, i) => ({ id: 950 + i, card: m.card!.id, pos: [m.pos[0]!, 0, m.pos[1]!] as const, yaw: m.yaw, vel: [0, 0, 0] as const })),
    probe: o.probe,
    order: -13,
    name: "rpg: the townsfolk stand-ins",
  });

  // the level-up ring: one mesh, shown while it rises
  const ring = new THREE.Mesh(part(new THREE.TorusGeometry(0.7, 0.05, 4, 24).rotateX(Math.PI / 2), "#f2c14e", 40, 1), mat);
  ring.visible = false;
  ring.name = "rpg: level-up ring";
  root.add(ring);
  let ringT = -1;
  // the notice line (the market lab's; the valley's HUD has a notice area)
  const notice = document.createElement("div");
  notice.dataset.rpgNotice = "";
  // announced politely without a status role: the screens own the page's one status region (a strict getByRole)
  notice.setAttribute("aria-live", "polite");
  notice.style.cssText = "position:fixed;left:50%;top:18%;transform:translateX(-50%);font:600 28px system-ui,sans-serif;color:#fbf6e8;text-shadow:0 2px 6px rgba(0,0,0,.6);pointer-events:none;opacity:0;z-index:800";
  if (o.notice !== false) document.body.appendChild(notice);
  let noticeT = -1;
  let notices = 0;
  let rings = 0;
  o.events.on("progress:level", (p) => {
    ringT = 0;
    noticeT = 0;
    notices++;
    rings++;
    notice.textContent = o.content.t("notice.levelUp", { level: p.level });
  });

  const scarf = o.hero.figure.chains.get("scarf") ?? null;
  const baseLinks = scarf?.links().base ?? 0;
  o.runtime.on(
    "late",
    (frame) => {
      // the scarf at the glyphs' length (read from the sim's state: it survives a restore)
      if (scarf) scarf.setLinks(baseLinks + r.scarfLinks());
      const real = Math.min(0.1, frame.realDt);
      if (ringT >= 0) {
        ringT += real;
        const k = Math.min(1, ringT / 0.9);
        const feet = o.hero.figure.root.position;
        ring.visible = k < 1;
        ring.position.set(feet.x, feet.y + 0.1 + k * 1.6, feet.z);
        ring.scale.setScalar(0.6 + k * 0.9);
        if (k >= 1) ringT = -1;
      }
      if (noticeT >= 0) {
        noticeT += real;
        notice.style.opacity = noticeT < 2.2 ? "1" : "0";
        if (noticeT > 2.6) noticeT = -1;
      }
    },
    { order: -12, name: "rpg: view" },
  );
  return {
    report: () => ({ stations, npcs: npcSet.report().standing, scarfLinks: scarf ? scarf.links().shown : null, notices, rings }),
    clearWarm: () => {
      ring.visible = false;
    },
  };
}
